import { useMemo, useState } from 'react';
import {
  UserCheck, UserX, Repeat2, UserPlus, Search, Gauge, Building2, Sparkles,
} from 'lucide-react';
import { usePost } from '@/lib/useData';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { fmtInt, fmtPct, fmtNum, fmtDate } from '@/lib/format';

const WINDOWS = [
  { v: '1', l: '1 mois' }, { v: '2', l: '2 mois' },
  { v: '3', l: '3 mois' }, { v: '6', l: '6 mois' },
];

const StatCard = ({ icon: Icon, label, value, sub, tone }) => {
  const iconWrap = tone === 'danger'
    ? 'bg-rose-500/10 text-rose-600'
    : tone === 'success'
      ? 'bg-emerald-500/10 text-emerald-600'
      : 'accent-bg-100 accent-text';
  const valueCls = tone === 'danger'
    ? 'text-rose-600'
    : tone === 'success'
      ? 'text-emerald-600'
      : tone === 'accent'
        ? 'accent-text'
        : 'text-foreground';
  return (
    <div className="rounded-2xl border bg-card p-4 card-shadow" data-testid="roster-kpi">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${iconWrap}`}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-xs">{label}</span>
      </div>
      <p className={`mt-2 font-display text-2xl font-semibold tabular ${valueCls}`}>{value}</p>
      {sub && <p className="mt-0.5 break-words text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
};

const AssocBadges = ({ names }) => (
  <div className="mt-1 flex flex-wrap items-center gap-1">
    {(names || []).map((a) => (
      <span key={a} className="inline-flex items-center gap-0.5 rounded-md bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
        <Building2 className="h-2.5 w-2.5" />{a}
      </span>
    ))}
  </div>
);

const RosterRow = ({ r, mode, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect?.(r.key)}
    data-testid="roster-row"
    className="flex w-full flex-wrap items-center gap-3 rounded-xl border bg-card p-3 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[color:var(--tenant-accent-300)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-400)]">
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="break-words font-display text-sm font-semibold text-foreground">{r.key}</span>
        {r.is_new && (
          <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600">
            <Sparkles className="h-2.5 w-2.5" /> Nouveau
          </span>
        )}
        {r.code && <span className="font-mono text-[10px] text-muted-foreground">{r.code}</span>}
      </div>
      <AssocBadges names={r.association_names} />
    </div>
    <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1">
      {mode === 'active' ? (
        <>
          <div className="text-right">
            <p className="font-display text-base font-semibold tabular text-foreground">{fmtInt(r.bs_reel)}</p>
            <p className="text-[10px] text-muted-foreground">BS validés</p>
          </div>
          <div className="text-right">
            <p className="font-display text-base font-semibold tabular accent-text">{fmtNum(r.bs_par_jour, 2)}</p>
            <p className="text-[10px] text-muted-foreground">BS / jour</p>
          </div>
          <div className="hidden text-right sm:block">
            <p className="text-xs font-medium text-foreground">il y a {fmtInt(r.days_since_last)} j</p>
            <p className="text-[10px] text-muted-foreground">dernier BS</p>
          </div>
        </>
      ) : (
        <>
          <div className="text-right">
            <p className="text-xs font-medium text-rose-600">Inactif depuis {fmtInt(r.days_since_last)} j</p>
            <p className="text-[10px] text-muted-foreground">dernier BS le {fmtDate(r.last_date)}</p>
          </div>
          <div className="hidden text-right sm:block">
            <p className="font-display text-base font-semibold tabular text-foreground">{fmtInt(r.bs_reel)}</p>
            <p className="text-[10px] text-muted-foreground">BS validés (histo.)</p>
          </div>
        </>
      )}
    </div>
  </button>
);

export const RosterPanel = ({ body, onSelect }) => {
  const [windowMonths, setWindowMonths] = useState('3');
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);

  const reqBody = useMemo(() => ({ ...body, window_months: Number(windowMonths) }), [body, windowMonths]);
  const { data, isLoading, isError } = usePost('/admin/recruiters/roster', reqBody);

  const list = useMemo(() => {
    let arr = (tab === 'active' ? data?.active : data?.inactive) || [];
    if (onlyNew) arr = arr.filter((r) => r.is_new);
    const q = search.trim().toLowerCase();
    return q
      ? arr.filter((r) => r.key.toLowerCase().includes(q) || (r.code || '').toLowerCase().includes(q))
      : arr;
  }, [data, tab, search, onlyNew]);

  // How many "new" recruiters are in the currently selected bucket (active/inactive).
  const newInBucket = useMemo(() => {
    const arr = (tab === 'active' ? data?.active : data?.inactive) || [];
    return arr.filter((r) => r.is_new).length;
  }, [data, tab]);

  if (isError) {
    return <EmptyState title="Accès restreint" body="Cette analyse est réservée aux administrateurs SHARITI." />;
  }
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} height="h-16" />)}
        </div>
        <CardSkeleton height="h-96" />
      </div>
    );
  }
  if (!data || !data.total) {
    return <EmptyState title="Aucun recruteur" body="Aucune donnée de vivier pour ce périmètre." />;
  }

  const retention = data.retention_rate || 0;

  return (
    <div className="space-y-5" data-testid="roster-panel">
      {/* Intro + window selector */}
      <div className="flex flex-col gap-3 rounded-2xl border p-4 accent-bg-soft sm:flex-row sm:items-center sm:justify-between" data-testid="roster-intro">
        <div className="flex items-start gap-3">
          <Repeat2 className="mt-0.5 h-5 w-5 shrink-0 accent-text" />
          <p className="text-sm text-foreground">
            Vivier des recruteurs <span className="font-semibold">actifs</span> vs. <span className="font-semibold">sortis</span>.
            Un recruteur est <span className="font-semibold">actif</span> si son dernier bulletin date de moins de {windowMonths} mois
            {' '}(réf. {fmtDate(data.reference_date)}, seuil {fmtDate(data.cutoff_date)}).
          </p>
        </div>
        <div className="shrink-0">
          <span className="mb-1 block text-[11px] text-muted-foreground">Fenêtre d'activité</span>
          <Select value={windowMonths} onValueChange={setWindowMonths}>
            <SelectTrigger className="h-9 w-[150px] rounded-xl bg-card" data-testid="roster-window-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => <SelectItem key={w.v} value={w.v}>{w.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Gauge} label="Taux de rétention" value={fmtPct(retention)} sub={`${fmtInt(data.active_count)} actifs / ${fmtInt(data.total)}`} tone="accent" />
        <StatCard icon={UserCheck} label="Vivier actif" value={fmtInt(data.active_count)} sub="recruteurs opérationnels" tone="success" />
        <StatCard icon={UserX} label="Turnover (sortis)" value={fmtInt(data.inactive_count)} sub="sans bulletin récent" tone="danger" />
        <StatCard icon={UserPlus} label="Nouveaux" value={fmtInt(data.new_count)} sub="1re activité dans la fenêtre" />
      </div>

      {/* Retention bar */}
      <div className="rounded-2xl border bg-card p-4 card-shadow">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="font-medium text-foreground">Rétention du vivier</span>
          <span className="tabular font-semibold accent-text">{fmtPct(retention)}</span>
        </div>
        <Progress value={retention * 100} className="h-2" data-testid="roster-retention-bar" />
      </div>

      {/* Tabs + filters */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-xl" data-testid="roster-tabs">
            <TabsTrigger value="active" className="gap-1.5 rounded-lg" data-testid="roster-tab-active">
              <UserCheck className="h-3.5 w-3.5" /> Vivier actif ({fmtInt(data.active_count)})
            </TabsTrigger>
            <TabsTrigger value="inactive" className="gap-1.5 rounded-lg" data-testid="roster-tab-inactive">
              <UserX className="h-3.5 w-3.5" /> Sortis ({fmtInt(data.inactive_count)})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={() => setOnlyNew((v) => !v)}
            aria-pressed={onlyNew}
            data-testid="roster-only-new"
            title="Afficher uniquement les recruteurs récemment arrivés (badge Nouveau)"
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2 text-xs font-medium transition-[background-color,border-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-400)] ${onlyNew ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700' : 'bg-card text-muted-foreground hover:border-[color:var(--tenant-accent-300)]'}`}>
            <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
            Nouveaux uniquement
            <span className={`tabular rounded-full px-1.5 py-0.5 text-[10px] ${onlyNew ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground'}`}>{fmtInt(newInBucket)}</span>
          </button>
          <div className="relative min-w-0 sm:max-w-xs sm:flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un recruteur…"
              className="rounded-xl pl-9" data-testid="roster-search" />
          </div>
        </div>
      </div>

      {/* List */}
      {list.length === 0 ? (
        <EmptyState title="Aucun recruteur" body="Aucun recruteur ne correspond à cette recherche." />
      ) : (
        <div className="max-h-[560px] space-y-2 overflow-y-auto pr-1" data-testid="roster-list">
          {list.map((r) => <RosterRow key={r.key} r={r} mode={tab} onSelect={onSelect} />)}
        </div>
      )}
    </div>
  );
};
